import { alterarSenhaSchema } from '@mymaxx/shared';
import { Aviso, Botao, CampoTexto } from '@mymaxx/ui';
import { LogOut, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ErroApi, authApi } from '../lib/api';
import { useSessao } from '../lib/sessao';

/** Conta do próprio usuário da equipe: senha, autenticador e sessões. */
export function MinhaConta() {
  const { usuario, definir, limpar } = useSessao();
  const navegar = useNavigate();
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function alterarSenha(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    setOk(null);
    const parse = alterarSenhaSchema.safeParse({ senhaAtual, novaSenha });
    if (!parse.success) {
      setErro(parse.error.issues[0]?.message ?? 'Confira os campos');
      return;
    }
    setEnviando(true);
    try {
      definir(await authApi.alterarSenha(parse.data));
      setSenhaAtual('');
      setNovaSenha('');
      setOk('Senha alterada. As outras sessões foram encerradas.');
    } catch (e) {
      setErro(e instanceof ErroApi ? e.mensagem : 'Não foi possível alterar a senha');
    } finally {
      setEnviando(false);
    }
  }

  async function sairDeTodos() {
    if (!confirm('Encerrar a sessão em todos os dispositivos, inclusive este?')) return;
    try {
      await authApi.logoutTodos();
    } finally {
      limpar();
      navegar('/login', { replace: true });
    }
  }

  return (
    <div className="max-w-xl space-y-6">
      <header>
        <h1 className="text-xl font-semibold">Minha conta</h1>
        <p className="text-sm text-neutral-500">
          {usuario?.nome} · {usuario?.email} · perfil {usuario?.perfil.nome}
        </p>
      </header>

      <section className="rounded-card border border-neutral-200 p-5 dark:border-neutral-800">
        <h2 className="mb-3 text-sm font-medium">Alterar senha</h2>
        <form className="space-y-3" onSubmit={alterarSenha} noValidate>
          <CampoTexto
            rotulo="Senha atual"
            type="password"
            autoComplete="current-password"
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
          />
          <CampoTexto
            rotulo="Nova senha"
            type="password"
            autoComplete="new-password"
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            ajuda="Mínimo de 10 caracteres, sem usar seu nome ou e-mail."
          />
          {erro && <Aviso tipo="erro">{erro}</Aviso>}
          {ok && <Aviso tipo="sucesso">{ok}</Aviso>}
          <Botao type="submit" carregando={enviando}>
            Salvar nova senha
          </Botao>
        </form>
      </section>

      <section className="rounded-card border border-neutral-200 p-5 dark:border-neutral-800">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-medium">
          <ShieldCheck
            size={16}
            className={usuario?.totpAtivo ? 'text-green-600' : 'text-amber-600'}
          />
          Verificação em duas etapas
        </h2>
        <p className="text-sm text-neutral-500">
          {usuario?.totpAtivo
            ? 'Ativa. A cada login pedimos o código do seu aplicativo autenticador.'
            : 'Não configurada. Ative para proteger seu acesso com um código que muda a cada 30 segundos.'}
        </p>
        {!usuario?.totpAtivo && (
          <Botao className="mt-3" onClick={() => navegar('/configurar-2fa')}>
            Configurar agora
          </Botao>
        )}
        {usuario?.totpAtivo && (
          <p className="mt-2 text-xs text-neutral-500">
            Trocou de celular? Peça a um administrador para redefinir seu autenticador.
          </p>
        )}
      </section>

      <section className="rounded-card border border-neutral-200 p-5 dark:border-neutral-800">
        <h2 className="mb-1 text-sm font-medium">Sessões</h2>
        <p className="text-sm text-neutral-500">
          Use isto se esqueceu o painel aberto em outro computador. Você precisará entrar de novo.
        </p>
        <Botao variante="perigo" className="mt-3" onClick={sairDeTodos}>
          <LogOut size={16} /> Sair de todos os dispositivos
        </Botao>
      </section>
    </div>
  );
}
